'use client';

import { useId, useState } from 'react';
import { motion } from 'motion/react';
import { RiArrowDownSLine } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { Link } from '@/i18n/navigation';
import type { HelpGroupView } from '@/lib/help/catalog';
import { spring } from '@/lib/motion';

/**
 * Yardım merkezinin konu gezintisi — makale sayfasının sol sütunu.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Deri yine `.art-toc`
 * ═══════════════════════════════════════════════════════════════════════════
 * Yasal metinlerin ve blog yazılarının içindekiler sütunu ne yapıyorsa aynısı:
 * geniş ekranda sabitlenen, sağında ince bir hat olan bir sütun; dar ekranda
 * gövdenin üstünde çerçeveli bir kutu. Yeni bir kenar çubuğu yazmak, sitenin
 * iki farklı yerinde iki farklı okuma düzeni sunmak olurdu.
 *
 * Fark: burada liste GRUPLU (on kategori) ve otuz sekiz satır. Bu yüzden iki
 * ek davranış var ve ikisi de `globals.css` → "Yardım merkezi"nde:
 *   • geniş ekranda sütun kendi içinde kayıyor (sabitlenmiş bir kutunun taşan
 *     yarısı erişilemez olurdu),
 *   • dar ekranda liste KAPALI başlıyor ve bir düğmeyle açılıyor — otuz sekiz
 *     satır, makaleyi ekranın iki katı aşağı iterdi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Açılır davranış CSS ile, KOŞULLU RENDER ile değil
 * ═══════════════════════════════════════════════════════════════════════════
 * `<nav>` her zaman DOM'da; görünürlüğü `data-open` + medya sorgusu
 * belirliyor. Koşullu render edilseydi sunucu ile istemcinin ilk karesi
 * ayrışır ve hidrasyon kırılırdı — bu depoda bir kez yaşanmış bir hata.
 * Geniş ekranda `data-open` hiç okunmuyor: kural medya sorgusunun içinde
 * sonradan geldiği için sütun her hâlükârda açık.
 */
export function HelpNav({
  groups,
  current,
}: {
  groups: readonly HelpGroupView[];
  /** Açık makalenin slug'ı. Hub'da verilmiyor. */
  current?: string;
}) {
  const t = useTranslations('help');
  const uid = useId();
  const [open, setOpen] = useState(false);

  return (
    <div className="help-nav-shell" data-open={open || undefined}>
      {/* Düğme yalnız dar ekranda görünüyor (globals.css). `aria-expanded`
          her genişlikte doğru: geniş ekranda düğme basılmıyor bile. */}
      <button
        type="button"
        className="help-nav-toggle"
        aria-expanded={open}
        aria-controls={`${uid}-nav`}
        onClick={() => setOpen((value) => !value)}
      >
        <span>{t('nav.mobileSummary')}</span>
        <RiArrowDownSLine className="help-nav-chevron" size={18} aria-hidden="true" />
      </button>

      <nav className="art-toc help-nav" id={`${uid}-nav`} aria-label={t('nav.label')}>
        <Link className="help-nav-all" href="/help">
          {t('nav.all')}
        </Link>

        {groups.map((group) => (
          <div className="help-nav-group" key={group.id}>
            <p className="art-toc-title help-nav-title">{t(`categories.${group.id}.title`)}</p>
            <ul className="art-toc-list">
              {group.articles.map((article) => {
                const active = article.slug === current;

                return (
                  <li className="art-toc-item" key={article.slug}>
                    <Link
                      className="art-toc-link"
                      href={`/help/${article.slug}`}
                      aria-current={active ? 'page' : undefined}
                    >
                      {/* İşaret `layoutId` ile makaleden makaleye TAŞINIYOR:
                          "aynı işaret, şimdi burada". */}
                      {active ? (
                        <motion.span
                          className="art-toc-mark"
                          aria-hidden="true"
                          layoutId={`${uid}-help-nav`}
                          transition={spring.default}
                        />
                      ) : null}
                      {/* Metin kendi katmanında: işaret `absolute` ve
                          sarmalayıcı olmadan metnin ÜSTÜNE binerdi. */}
                      <span className="art-toc-text">{article.title}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </div>
  );
}
